import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { Check, Copy, Loader2, Table2 } from "lucide-react";

export interface TableRun {
  text: string;
  sup?: boolean;
}

export interface PaperTableRow {
  cells: TableRun[][];
  spanning?: boolean;
}

export interface PaperTable {
  label: string;
  caption: string;
  page: number;
  rebuilt: boolean;
  columns: number;
  header: PaperTableRow[];
  rows: PaperTableRow[];
  notes: string | null;
  /** Where the caption starts in the paper text, when it could be found. */
  textOffset: number | null;
}

/** The paper's tables, rebuilt from the PDF layout on the server (no AI). */
export function usePaperTables(studyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["paper-tables", studyId],
    queryFn: () => api.get<{ hasPdf: boolean; tables: PaperTable[] }>(`/extraction/${studyId}/tables`),
    enabled: !!studyId && enabled,
    staleTime: 10 * 60 * 1000,
  });
}

function cellPlain(cell: TableRun[]): string {
  return cell.map((r) => r.text).join("").trim();
}

/** Tab-separated, so it pastes into a spreadsheet as rows and columns. */
function toTsv(table: PaperTable): string {
  const line = (row: PaperTableRow) =>
    (row.spanning ? [cellPlain(row.cells[0] ?? [])] : row.cells.map(cellPlain)).map((c) => c.replace(/\s+/g, " ")).join("\t");
  return [...table.header, ...table.rows].map(line).join("\n");
}

function Cell({ runs }: { runs: TableRun[] }) {
  return (
    <>
      {runs.map((r, i) => (r.sup ? <sup key={i}>{r.text}</sup> : <span key={i}>{r.text}</span>))}
    </>
  );
}

function TableCard({ table, focusAt }: { table: PaperTable; focusAt: number | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (focusAt === null || !ref.current) return;
    ref.current.scrollIntoView({ block: "start" });
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1500);
    return () => clearTimeout(t);
  }, [focusAt]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(toTsv(table));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable: nothing to do */
    }
  };

  return (
    <div
      ref={ref}
      className={`rounded-lg border bg-background scroll-mt-3 transition-shadow ${flash ? "ring-2 ring-yellow-400" : ""}`}
    >
      <div className="flex items-start gap-3 px-3 py-2 border-b">
        <p className="text-xs leading-snug flex-1">
          <span className="font-semibold">{table.label}</span>
          {table.caption.length > table.label.length && (
            <span className="text-muted-foreground">{table.caption.slice(table.label.length).replace(/^\s*[.:|—–-]?\s*/, ": ")}</span>
          )}
        </p>
        <span className="text-[11px] text-muted-foreground whitespace-nowrap">Page {table.page}</span>
        {table.rebuilt && (
          <button
            onClick={copy}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground whitespace-nowrap"
            title="Copy as tab-separated text, ready to paste into a spreadsheet"
          >
            {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>

      {table.rebuilt ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              {table.header.map((row, i) => (
                <tr key={i} className="bg-muted/60">
                  {row.cells.map((cell, j) => (
                    <th key={j} className="text-left font-medium align-bottom px-2 py-1.5 border-b">
                      <Cell runs={cell} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.rows.map((row, i) =>
                row.spanning ? (
                  <tr key={i} className="bg-muted/30">
                    <td colSpan={table.columns} className="px-2 py-1 font-medium border-b">
                      <Cell runs={row.cells[0] ?? []} />
                    </td>
                  </tr>
                ) : (
                  <tr key={i} className="hover:bg-muted/20">
                    {row.cells.map((cell, j) => (
                      <td key={j} className="align-top px-2 py-1 border-b">
                        <Cell runs={cell} />
                      </td>
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-3 py-3 text-xs text-muted-foreground">
          The layout of this table could not be rebuilt. It is on page {table.page} of the PDF.
        </p>
      )}

      {table.notes && <p className="px-3 py-2 text-[11px] text-muted-foreground border-t">{table.notes}</p>}
    </div>
  );
}

/**
 * The paper's tables as tables. Plain-text extraction scrambles them (a cell
 * that wraps onto several lines ends up mixed with its neighbours), so they
 * are rebuilt from where each piece of text sits on the PDF page.
 */
export function PaperTables({ studyId, focus }: { studyId: string; focus: { label: string; at: number } | null }) {
  const { data, isLoading, isError } = usePaperTables(studyId);
  const tables = data?.tables ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Finding the tables in the PDF
      </div>
    );
  }
  if (isError) return <p className="p-5 text-sm text-red-600">The tables could not be loaded.</p>;
  if (!data?.hasPdf) return <p className="p-5 text-sm text-muted-foreground">No PDF has been uploaded for this paper, so there are no tables to show.</p>;
  if (tables.length === 0) {
    return (
      <p className="p-5 text-sm text-muted-foreground">
        No tables were found in this PDF. Tables printed as images (and scanned papers) cannot be read as text: look under Images.
      </p>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
        <Table2 className="h-3.5 w-3.5 mt-px flex-shrink-0" />
        Rebuilt from the layout of the PDF, without AI. Check values against the PDF before relying on them, especially in tables with merged cells.
      </p>
      {tables.map((t) => (
        <TableCard key={t.label} table={t} focusAt={focus?.label === t.label ? focus.at : null} />
      ))}
    </div>
  );
}
